import { Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";

import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { isDevelopmentEnvironment } from "@/lib/is-development-environment";
import { lazyRoute } from "@/routes/lazy-route";

const MyClimateActionListLayout = lazyRoute(() => import("./my-climate-actions/my-climate-actions.layout"), "MyClimateActionListLayout");
const MyClimateActionsPage = lazyRoute(() => import("./my-climate-actions/my-climate-actions.page"), "MyClimateActionsPage");
const ClimateActionListRootLayout = lazyRoute(() => import("./climate-actions/climate-action-list-root.layout"), "ClimateActionListRootLayout");
const ClimateActionListLayout = lazyRoute(() => import("./climate-actions/(list)/climate-action-list.layout"), "ClimateActionListLayout");
const ClimateActionListAllPage = lazyRoute(() => import("./climate-actions/(list)/climate-action-list.all.page"), "ClimateActionListAllPage");
const ClimateActionListDashboardPage = lazyRoute(() => import("./climate-actions/(list)/climate-action-list.dashboard.page"), "ClimateActionListDashboardPage");
const ClimateActionListTimelinePage = lazyRoute(() => import("./climate-actions/(list)/climate-action-list-timeline.page"), "ClimateActionListTimelinePage");
const ClimateActionListBoardPage = lazyRoute(() => import("./climate-actions/(list)/climate-action-list-board.page"), "ClimateActionListBoardPage");
const ClimateActionUploadPage = lazyRoute(() => import("./climate-actions/climate-action-upload-page"), "ClimateActionUploadPage");
const ClimateActionListStandardPage = lazyRoute(() => import("./climate-actions/(list)/climate-action-list.standard.page"), "ClimateActionListStandardPage");
const ClimateActionListCreateSubLayout = lazyRoute(() => import("./climate-actions/(list)/climate-action-list.create-sub.layout"), "ClimateActionListCreateSubLayout");
const ClimateActionLayout = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action.layout"), "ClimateActionLayout");
const ClimateActionPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action.page"), "ClimateActionPage");
const ClimateActionDetailTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-detail-tab.page"), "ClimateActionDetailTabPage");
const ClimateActionFoerderscoutTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-foerderscout-tab.page"), "ClimateActionFoerderscoutTabPage");
const ClimateActionTaskListTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-task-list-tab.page"), "ClimateActionTaskListTabPage");
const ClimateActionProjectsTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-project-tab.page"), "ClimateActionProjectsTabPage");
const ClimateActionMilestonesTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-milestones-tab.page"), "ClimateActionMilestonesTabPage");
const ClimateActionDocumentsTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-documents-tab.page"), "ClimateActionDocumentsTabPage");
const ClimateActionIndicatorListTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-indicator-list-tab.page"), "ClimateActionIndicatorListTabPage");
const ClimateActionReductionTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-reduction-tab.page"), "ClimateActionReductionTabPage");
const ClimateActionActivityTabPage = lazyRoute(() => import("./climate-actions/$climateActionId/climate-action-activity-tab.page"), "ClimateActionActivityTabPage");
const StandardPageGate = () => {
  const { isEnabled: isCatalogueEnabled, loading: catalogueLoading } = useFeatureFlagSimple({
    id: "enable-climate-action-catalogue",
  });
  const { isEnabled: isSuggestionsEnabled, loading: suggestionsLoading } = useFeatureFlagSimple({
    id: "enable-climate-action-suggestions",
  });
  if (catalogueLoading || suggestionsLoading) {
    return (
      <div role="status" aria-busy="true" aria-live="polite" className="flex flex-1 items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  if (!isCatalogueEnabled && !isSuggestionsEnabled && !isDevelopmentEnvironment()) {
    return <Navigate to="all" replace />;
  }
  return <ClimateActionListStandardPage />;
};

const ClimateActionTrashPage = lazyRoute(() => import("@/domain/trash/routes/climate-action-trash.page"), "ClimateActionTrashPage");

export const myClimateActionRoutes = [
  {
    path: "my-climate-actions",
    element: <MyClimateActionListLayout />,
    children: [{ path: "", element: <MyClimateActionsPage /> }],
  },
];

export const climateActionRoutes = [
  {
    path: "climate-actions",
    element: <ClimateActionListRootLayout />,
    children: [
      {
        path: "",
        element: <ClimateActionListLayout />,
        children: [
          { path: "", element: <Navigate to="all" replace /> },
          { path: "all", element: <ClimateActionListAllPage /> },
          { path: "trash", element: <ClimateActionTrashPage /> },
          { path: "dashboard", element: <ClimateActionListDashboardPage /> },
          {
            path: "timeline",
            element: (
              <FeatureFlagToggle flag="enable-timeline-tab" fallback={<Navigate to="all" replace />}>
                <ClimateActionListTimelinePage />
              </FeatureFlagToggle>
            ),
          },
          {
            path: "board",
            element: (
              <FeatureFlagToggle flag="enable-board-tab" fallback={<Navigate to="all" replace />}>
                <ClimateActionListBoardPage />
              </FeatureFlagToggle>
            ),
          },
          {
            path: "",
            element: <ClimateActionListCreateSubLayout />,
            children: [
              { path: "upload", element: <ClimateActionUploadPage /> },
              {
                path: "standard",
                element: <StandardPageGate />,
              },
            ],
          },
        ],
      },
      {
        path: ":climateActionId",
        element: <ClimateActionLayout />,
        children: [
          {
            path: "",
            element: <ClimateActionPage />,
            children: [
              { path: "", element: <Navigate to="details" replace /> },
              { path: "details", element: <ClimateActionDetailTabPage /> },
              { path: "foerderscout", element: <ClimateActionFoerderscoutTabPage /> },
              { path: "tasks", element: <ClimateActionTaskListTabPage /> },
              { path: "projects", element: <ClimateActionProjectsTabPage /> },
              {
                path: "milestones",
                element: (
                  <FeatureFlagToggle flag="enable-milestone-tab">
                    <ClimateActionMilestonesTabPage />
                  </FeatureFlagToggle>
                ),
              },
              { path: "documents", element: <ClimateActionDocumentsTabPage /> },
              {
                path: "indicators",
                element: <ClimateActionIndicatorListTabPage />,
              },
              {
                path: "reduction",
                element: <ClimateActionReductionTabPage />,
              },
              { path: "activity", element: <ClimateActionActivityTabPage /> },
            ],
          },
        ],
      },
    ],
  },
];
